import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Calendar, AlertCircle, CheckCircle, Clock } from "lucide-react";
import type { Work } from "@/pages/Works";

interface WorkCardProps {
  work: Work;
  viewMode?: "grid" | "list";
}

export const WorkCard = ({ work, viewMode = "grid" }: WorkCardProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleCardClick = () => {
    // Save current filters to localStorage before navigating
    localStorage.setItem('obras-filters', location.search.substring(1));
    
    // Navigate to the work panel
    navigate(`/dashboard?obra=${work.id}`);
  };

  const getStatusConfig = () => {
    switch (work.status) {
      case "em-andamento":
        return {
          label: "Em andamento",
          bgColor: "bg-success",
          textColor: "text-white"
        };
      case "paralisada":
        return {
          label: "Paralisada",
          bgColor: "bg-warning",
          textColor: "text-white"
        };
      case "concluida":
        return {
          label: "Concluída",
          bgColor: "bg-muted",
          textColor: "text-foreground"
        };
    }
  };

  const statusConfig = getStatusConfig();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const formatExpectedEndDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const getDaysRemainingText = () => {
    if (work.status === "concluida") {
      return { text: "Concluída em 10/07", icon: CheckCircle, color: "text-success" };
    }
    if (work.daysRemaining < 0) {
      return { 
        text: `Paralisada há ${Math.abs(work.daysRemaining)} dias`, 
        icon: AlertCircle, 
        color: "text-destructive" 
      };
    }
    if (work.daysRemaining === 0) {
      return { text: "Vence hoje", icon: Clock, color: "text-warning" };
    }
    return { 
      text: `Faltam ${work.daysRemaining} dias`, 
      icon: Calendar, 
      color: "text-muted-foreground" 
    };
  };

  const timeInfo = getDaysRemainingText();
  const TimeIcon = timeInfo.icon;

  return (
    <Card 
      className="shadow-card hover:shadow-card-hover transition-all duration-200 cursor-pointer hover:-translate-y-1"
      onClick={handleCardClick}
    >
      <CardContent className={viewMode === "list" ? "p-2" : "p-4"}>
        <div className="flex gap-4">
          {/* Imagem da obra */}
          <div className={`${viewMode === "list" ? "w-10 h-10" : "w-16 h-16"} bg-muted rounded-lg flex-shrink-0 overflow-hidden`}>
            <img 
              src="/placeholder.svg" 
              alt={work.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Conteúdo principal */}
          <div className="flex-1 min-w-0">
            <div className={viewMode === "list" ? "flex items-start justify-between mb-1" : "flex items-start justify-between mb-2"}>
              <div className="flex-1 min-w-0">
                {/* Nome da obra */}
                <h3 className={viewMode === "list" ? "font-semibold text-sm text-foreground mb-1 truncate" : "font-semibold text-base text-foreground mb-1 truncate"}>
                  {work.name}
                </h3>
                
                {/* Cliente e Cidade - apenas no modo grid */}
                {viewMode === "grid" && (
                  <>
                    <p className="text-sm text-muted-foreground mb-1">
                      Cliente: {work.client}
                    </p>
                    
                    <p className="text-sm text-muted-foreground mb-3">
                      {work.city}
                    </p>
                  </>
                )}
              </div>

              {/* Badge de status */}
              <Badge 
                className={`${statusConfig.bgColor} ${statusConfig.textColor} border-0 text-xs`}
              >
                {statusConfig.label}
              </Badge>
            </div>

            {/* Progresso — vem das tarefas concluídas da obra. Sem tarefas
                vinculadas não há o que medir: dizemos isso em vez de mostrar
                uma barra com número inventado. */}
            <div className={viewMode === "list" ? "mb-1" : "mb-3"}>
              <div className="flex items-center justify-between mb-1">
                <span className={viewMode === "list" ? "text-xs text-muted-foreground" : "text-sm text-muted-foreground"}>Progresso</span>
                {work.progress === null ? (
                  <span className={viewMode === "list" ? "text-xs text-muted-foreground" : "text-sm text-muted-foreground"}>
                    sem tarefas
                  </span>
                ) : (
                  <span
                    className={viewMode === "list" ? "text-xs font-semibold text-foreground" : "text-sm font-semibold text-foreground"}
                    title={`${work.tarefasConcluidas} de ${work.tarefasTotal} tarefas concluídas`}
                  >
                    {work.progress}%
                  </span>
                )}
              </div>
              <Progress
                value={work.progress ?? 0}
                className={viewMode === "list" ? "h-1.5" : "h-2"}
              />
            </div>

            {/* Informações de tempo */}
            <div className="flex items-center justify-between">
              <div className={viewMode === "list" ? "flex items-center gap-1 text-xs" : "flex items-center gap-1 text-sm"}>
                <TimeIcon className={viewMode === "list" ? `h-3 w-3 ${timeInfo.color}` : `h-4 w-4 ${timeInfo.color}`} />
                <span className={timeInfo.color}>
                  {timeInfo.text}
                </span>
              </div>
              
              {/* Previsão de término - alinhada à direita */}
              <div className={viewMode === "list" ? "flex items-center gap-1 text-xs" : "flex items-center gap-1 text-sm"}>
                <Calendar className={viewMode === "list" ? "h-3 w-3 text-muted-foreground" : "h-4 w-4 text-muted-foreground"} />
                <span className="text-muted-foreground">
                  Prev. término: {formatExpectedEndDate(work.expectedEndDate)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};